import React, { useRef, useState, useLayoutEffect, useCallback } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';

import { PrintSize } from '../prop-types';
import Spinner from '../../_utils/spinner';
import { PandaViewPreviewContainer } from './panda-view-preview-container';
import PhotoPreviewWarning from './photo-quality/photo-preview-warning';
import BlurhashCanvas from './panda-view-simple-previews/blurhash-canvas';
import { computeDisplaySize } from './panda-view-simple-previews/panda-view-geometry';
import { usePrintsConfigStore } from '../stores';

// Must match PandaView's 150 ms draw setTimeout so the overlay stays visible until
// the canvas has painted, then fades out over 300 ms.
const OVERLAY_TRANSITION_DELAY_MS = 150;
const OVERLAY_TRANSITION_DURATION_MS = 300;
const ThumbnailLoadingSpinner = () => (
  <div
    data-test-id="thumbnail-loading-spinner"
    className="panda-preview-overlay bottom-left ml-2 mb-2">
    <div className="spinner-border spinner-border-sm" role="status" />
  </div>
);

const PandaViewSimplePreviews = (props) => {
  const {
    thumb_url: thumbUrl,
    blurhash,
    thumbnailFailed,
    fx,
    raw_dimensions: rawDimensions,
    showSize,
    size,
    isWallet,
    quantity,
    showQuantity,
    showPreviewWarning,
    recommendedPrintSizeTextForPreview,
    previewMaxPx,
  } = props;

  const { printsBuilderBlurhashDisabled } = usePrintsConfigStore(
    (state) => state.features,
  );

  // has the photo got blurhash string and feature not disabled
  const showBlurhash = !!(blurhash && !printsBuilderBlurhashDisabled);

  // -- Overlay state (basket-load and upload transition)
  // Show a blurhash overlay while PandaView draws the image onto its canvas.
  // Basket-load: initialized true so the overlay is visible immediately.
  // Upload transition: initialized false, then set true via useLayoutEffect
  //   when thumb_url arrives.
  // Wallet prints are excluded as they print two adjacent PandaViews.
  const [overlayMounted, setOverlayMounted] = useState(
    showBlurhash && !!thumbUrl && !isWallet,
  );
  const [overlayFading, setOverlayFading] = useState(false);

  // -- Upload transition: route through the overlay path
  // When a photo starts without a thumbnail (upload path) and then receives
  // thumb_url, show the overlay rather than a bare opacity fade-in. This keeps
  // the BlurhashCanvas visible while PandaView loads its image, preventing it
  // from briefly showing at its default 600×600 canvas size.
  // useLayoutEffect fires before the browser paints so the switch from the
  // placeholder to the overlay is atomic (no intermediate frame is painted).
  // Wallet is excluded for the same reason as above.
  const startedAsBlurhash = useRef(showBlurhash && !thumbUrl);
  useLayoutEffect(() => {
    if (thumbUrl && startedAsBlurhash.current && showBlurhash && !isWallet) {
      setOverlayMounted(true);
    }
  }, [thumbUrl, showBlurhash, isWallet]);

  const handleImageLoaded = useCallback(() => {
    // PandaView fires onImageLoaded before its 150 ms draw setTimeout, so we
    // rely on a matching CSS transition-delay to let the canvas paint first.
    setOverlayFading(true);
  }, []);

  const handleOverlayTransitionEnd = useCallback(() => {
    setOverlayMounted(false);
  }, []);

  // onImageLoaded never fires when PandaView's image errors, so without this the overlay
  // would sit permanently over the failed state the container renders underneath, showing
  // two stacked blurhash canvases.
  const handleImageError = useCallback(() => {
    setOverlayMounted(false);
  }, []);

  // -- Pre-constrain .panda-preview in overlay mode
  // PandaView initialises its canvas at 600×600 before drawing the image.
  // CSS scales this to a square (e.g. 150×150 on mobile), then after PandaView
  // draws the correct-ratio canvas (e.g. 600×400) it resizes to e.g. 150×100.
  // This layout shift is visible while the blurhash overlay is showing.
  // Setting explicit px dimensions + overflow:hidden on .panda-preview clips
  // PandaView's initial square canvas to the correct shape, preventing the shift.
  // previewMaxPx must match the CSS max-height/max-width applied to canvas via
  // preview-sizing: 150px for mobile, 180px for desktop.
  let pandaPreviewStyle;
  if (overlayMounted && rawDimensions && !isWallet) {
    const { width: pdW, height: pdH } = computeDisplaySize(
      rawDimensions,
      size.area,
    );
    const scale = previewMaxPx / Math.max(pdW, pdH);
    pandaPreviewStyle = {
      width: `${Math.round(pdW * scale)}px`,
      height: `${Math.round(pdH * scale)}px`,
      overflow: 'hidden',
    };
  }

  const overlayChildrenBase = (
    <>
      {showSize && (
        <div
          className={classNames('size-value', {
            'd-none': quantity === 0,
          })}>
          {size.title + (size.options.border === true ? ' Bordered' : '')}
        </div>
      )}
      {showQuantity && <div className="quantity-value">{quantity}</div>}
    </>
  );

  const overlayChildren = (
    <>
      {overlayChildrenBase}
      {showPreviewWarning && (
        <PhotoPreviewWarning
          recommendedPrintSizeText={recommendedPrintSizeTextForPreview}
        />
      )}
    </>
  );

  // Stops a flagged photo falling into the spinner branches below, which never resolve.
  // The container owns rendering the failed state itself.
  //
  // overlayChildren, not overlayChildrenBase: a photo can be both low resolution and
  // preview-failed, and it still gets printed, so the resolution warning must survive.
  if (thumbnailFailed) {
    return (
      <PandaViewPreviewContainer {...props}>
        {overlayChildren}
      </PandaViewPreviewContainer>
    );
  }

  // -- No thumbnail + no blurhash → existing spinner fallback
  if (!thumbUrl && !showBlurhash) {
    return <Spinner />;
  }

  // -- No thumbnail yet, but blurhash available
  if (!thumbUrl) {
    return (
      <PandaViewPreviewContainer
        {...props}
        placeholder={
          <BlurhashCanvas
            hash={blurhash}
            rawDimensions={rawDimensions}
            sizeArea={size.area}
            fx={fx}
          />
        }>
        {overlayChildrenBase}
        <ThumbnailLoadingSpinner />
      </PandaViewPreviewContainer>
    );
  }

  // -- Thumbnail available: blurhash overlay (basket-load and upload)
  // Show a blurhash overlay while PandaView draws the image on its canvas.
  // The overlay fades out (with OVERLAY_TRANSITION_DELAY_MS to let PandaView's draw
  // timeout complete), then unmounts once the transition ends.
  if (overlayMounted) {
    return (
      <PandaViewPreviewContainer
        {...props}
        onImageLoaded={handleImageLoaded}
        onImageError={handleImageError}
        pandaPreviewStyle={pandaPreviewStyle}>
        {overlayChildren}
        <div
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            zIndex: 1,
            pointerEvents: 'none',
            opacity: overlayFading ? 0 : 1,
            transition: overlayFading
              ? `opacity ${OVERLAY_TRANSITION_DURATION_MS}ms ease ${OVERLAY_TRANSITION_DELAY_MS}ms`
              : 'none',
          }}
          onTransitionEnd={handleOverlayTransitionEnd}>
          <BlurhashCanvas
            hash={blurhash}
            rawDimensions={rawDimensions}
            sizeArea={size.area}
            fx={fx}
            fill
          />
        </div>
      </PandaViewPreviewContainer>
    );
  }

  // - Normal case
  return (
    <PandaViewPreviewContainer {...props}>
      {overlayChildren}
    </PandaViewPreviewContainer>
  );
};

PandaViewSimplePreviews.propTypes = {
  thumb_url: PropTypes.string,
  blurhash: PropTypes.string,
  thumbnailFailed: PropTypes.bool,
  fx: PropTypes.string,
  raw_dimensions: PropTypes.shape({ w: PropTypes.number, h: PropTypes.number }),
  isWallet: PropTypes.bool.isRequired,
  showSize: PropTypes.bool,
  size: PrintSize.isRequired,
  showQuantity: PropTypes.bool.isRequired,
  quantity: PropTypes.number.isRequired,
  showPreviewWarning: PropTypes.bool,
  recommendedPrintSizeTextForPreview: PropTypes.string,
  previewMaxPx: PropTypes.number,
};

PandaViewSimplePreviews.defaultProps = {
  showSize: false,
  thumb_url: null,
  blurhash: null,
  thumbnailFailed: false,
  fx: null,
  raw_dimensions: null,
  showPreviewWarning: false,
  recommendedPrintSizeTextForPreview: '',
  previewMaxPx: 150,
};

export default PandaViewSimplePreviews;
